import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from 'react-router'
import { ProjetosFormInicial } from "src/components/projetos/FormInicial";
import { CarrinhosProvider } from "src/providers/loja/CarrinhosProvider";
import { formState$ } from "src/store/vendas/projetos/store";
import { DEFAULT_PROJETO, DEFAULT_PROJETO_AVULSO, INVERTER_MAX_POWER_ATTR, POTENCIA_MINIMA_VERTICAL } from "./constants";
import { Gerador, Option, Projeto, SegmentoType, VENDA } from "./types";
import { CacheType, componentes$, getComponentes } from "src/store/global.store";
import { useObservable } from "src/framework/store/storeUtil";
import { useHistory } from "react-router-dom";
import { MODULO_POWER_ATTR } from "./constants";
import { notifierStore } from "src/framework/store/NotifierStore";
import { Componente, ID_MODULO, inversoresMap } from "../componentes/types";
import { ProjetosFormInicialAvulso } from "./FormInicialAvulso";
import { estados$ } from "src/store/global.store";

const init = async () => {
  await Promise.all([
    getComponentes(),
  ])
}

const carrinhoProvider = new CarrinhosProvider()
const ProjetosFormInicialVerificaKit = () => {
  const location = useLocation()
  const history = useHistory();
  const [venda, setVenda] = useState<any>(null);
  const estadoListState = useObservable(estados$);

  const componentes = useObservable<CacheType<Componente[]>>(componentes$)
  const formState = useObservable(formState$)

  const carrinhoJaVincudoAlerta = useCallback(()=>{
    notifierStore.setNotifier({
      title: 'Atenção',
      content: 'Carrinho já vinculado a projeto',
      onClose: () => {
        notifierStore.setOpen(false)
        history.replace('/projetos/menu-projetos')
      },
      onFinishTimeout: () => {
        history.replace('/projetos/menu-projetos')
      },
      timeOut: 6000,
    })
    notifierStore.setOpen(true)
  },[history])

  const carrinhoNaoEncontrado = useCallback(()=>{
    notifierStore.setNotifier({
      title: 'Atenção',
      content: 'Carrinho não encontrado',
      onClose: () => {
        notifierStore.setOpen(false)
        history.replace('/projetos/menu-projetos')
      },
      onFinishTimeout: () => {
        history.replace('/projetos/menu-projetos')
      },
      timeOut: 6000,
    })
    notifierStore.setOpen(true)
  },[history])

  const segmentoVertical = (offgrid:boolean): SegmentoType => {
    if (offgrid) return 'vertical_offgrid';
    else return 'vertical_ongrid';
  }

  const buscarCarrinho = useCallback(async (guid: string)=>{
    const carrinho = await carrinhoProvider.buscarCarrinhoPorGuid(guid)
    if (!carrinho) {
      carrinhoNaoEncontrado()
      return;
    }
    setVenda(carrinho.venda)

    if (carrinho.codigo_venda) {
      carrinhoJaVincudoAlerta()
      return
    }
    switch(carrinho.venda){
      case VENDA.AVULSO:
        const valorTotalAvulsos = carrinho?.avulsos?.reduce((total: any, produto: any) => {
          return total + (produto?.valor * produto?.quantidade);
        }, 0);
        const projetoAvulso: Projeto = {
          ...DEFAULT_PROJETO_AVULSO,
          tipo: 'avulso',
          carrinho_id: carrinho.id,
          valor: valorTotalAvulsos,
          valor_projeto: valorTotalAvulsos,
          avulsos: carrinho.avulsos,
          estado: estadoListState?.data?.find((e: any)=>e.id === carrinho.estado_id),
        }
    
        formState$.next(projetoAvulso)
        break;
      default:
        const isOffGrid = carrinho?.itens?.every(item => item.segmento === 'off')
        let segmento = DEFAULT_PROJETO.segmento
    
        if (isOffGrid) {
          segmento = 'distribuicao_offgrid'
        }
    
        let percentual_comissao_distribuidor = 0;
        let temInversor = false;
        let temModulo = false;
        let tipo_inversor: Option<number> | null = null
    
        const geradores_kits: Gerador[] = carrinho.itens.map((item): Gerador => {
          const quantidadeItem = item.quantidade || 0
          
          percentual_comissao_distribuidor += item.comissao_distribuidor;
    
          return {
            id: item.id,
            items: item.itens.map(innerItem => {
              const componenteDetails = componentes?.data.find(componente => componente.id === innerItem.componente_id)
              let potencia = 0
    
              if (componenteDetails?.categoria_componentes_id === ID_MODULO) {
                temModulo = true
                potencia = Number(componenteDetails?.atributos?.[MODULO_POWER_ATTR] || 0)
              }
    
              if (componenteDetails?.categoria_componentes_id && inversoresMap[componenteDetails?.categoria_componentes_id]) {
                temInversor = true
                potencia = Number(componenteDetails?.atributos?.[INVERTER_MAX_POWER_ATTR] || 0)
                tipo_inversor = inversoresMap[componenteDetails?.categoria_componentes_id]
              }
    
              return {
                id: Number(innerItem.componente_id || 0),
                altura: Number(componenteDetails?.altura || 0),
                categoria_componentes_id: Number(componenteDetails?.categoria_componentes_id || 0),
                componente_id: Number(innerItem.componente_id || 0),
                comprimento: Number(componenteDetails?.comprimento || 0),
                descricao: componenteDetails?.descricao || '',
                largura: Number(componenteDetails?.largura || 0),
                nome: componenteDetails?.nome || '',
                peso: Number(componenteDetails?.peso || 0),
                potencia,
                quantidade: Number(innerItem.quantidade || 0) * quantidadeItem,
                valor: Number(innerItem.valor || 0),
                valor_unitario: 0,
              }
            }),
            nome: item.nome,
            quantidade: quantidadeItem,
          }
        })
    
        const showRoiPayback = temInversor && temModulo && carrinho.itens.length === 1 && carrinho.itens[0].quantidade === 1;
    
        const potenciaTotal = geradores_kits?.reduce((total, kit) => {
          const items = kit?.items?.filter(item => item?.categoria_componentes_id === ID_MODULO);
          const kitPotencia = items.reduce((kitTotal, item) => {
            return kitTotal + (item?.potencia * item?.quantidade);
          }, 0);
          return total + kitPotencia;
        }, 0);
    
        if ((potenciaTotal / 1000.0) > POTENCIA_MINIMA_VERTICAL) {
          segmento = segmentoVertical(isOffGrid);
        }
    
        const projeto: Projeto = {
          ...DEFAULT_PROJETO,
          geradores_kits,
          tipo: 'kit',
          carrinho_id: carrinho.id,
          showRoiPayback,
          segmento,
          tipo_inversor: tipo_inversor || null,
          percentual_comissao_distribuidor,
        }
    
        formState$.next(projeto)
        break;
    }
  },[carrinhoJaVincudoAlerta, carrinhoNaoEncontrado, componentes?.data])

  const guid = useMemo(()=>{
    const querys = new URLSearchParams(location.search);
    return querys.get('guid')
  },[location.search])

  useEffect(()=>{
    init().then(()=>{
      if (guid && componentes?.data.length) {
        buscarCarrinho(guid);
      }
    })
  },[buscarCarrinho, guid, componentes, location])

  if (guid && !formState?.tipo) return null;

  return (
    <div className="container">
      <div className="row">
        <div className="X12">
          {venda === "avulso" ? <ProjetosFormInicialAvulso/> :<ProjetosFormInicial segmento={formState?.segmento} />}
        </div>
      </div>
    </div>
  );
};

export { ProjetosFormInicialVerificaKit };
